import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { TrendingUp, Users, DollarSign, Plus, UserCheck, UserPlus, Search, ArrowLeft, Handshake, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useReferrals, CreateContactWithReferralData, DuplicateCheckResult } from '@/hooks/useReferrals';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ContactSearchCombobox } from '@/components/common/ContactSearchCombobox';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useSalesUsers } from '@/hooks/useSalesUsers';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';

interface ReferralsTabProps {
  contactId: string;
}

export function ReferralsTab({ contactId }: ReferralsTabProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { 
    contactReferralsMade, 
    dealReferralsMade, 
    referredBy, 
    referrerRevenue, 
    isLoading, 
    createReferral,
    createDealReferral,
  } = useReferrals(contactId);
  
  // Deal created state
  const [createdDealId, setCreatedDealId] = useState<string | null>(null);
  const [showDealCreatedDialog, setShowDealCreatedDialog] = useState(false);

  const handleDealCreated = (dealId: string) => {
    setCreatedDealId(dealId);
    setShowDealCreatedDialog(true);
  };

  const getContextLabel = (context: string) => {
    const key = `referrals.contexts.${context}` as const;
    const translated = t(key);
    return translated === key ? context : translated;
  };

  if (isLoading) {
    return <div className="p-6">{t('referrals.loading')}</div>;
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(value);
  };

  const getDealStageColor = (stage: string) => {
    const normalizedStage = (stage ?? '').toLowerCase();
    switch (normalizedStage) {
      case 'won':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'lost':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
    }
  };

  const totalContactReferrals = contactReferralsMade.length;
  const totalDealReferrals = dealReferralsMade.length;

  return (
    <div className="space-y-6 p-6">
      {/* Seção: Indicado Por */}
      {referredBy && (
        <Card className="p-6 bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-900">
          <div className="flex items-center gap-3 mb-4">
            <UserCheck className="h-5 w-5 text-blue-600" />
            <h3 className="text-lg font-semibold">{t('referrals.referredBy')}</h3>
            <Badge variant="outline" className="ml-auto">
              Indicação de Contato
            </Badge>
          </div>
          <div className="flex items-center gap-3 p-3 bg-background rounded-lg">
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
              <span className="text-lg font-medium text-blue-600">
                {referredBy.referrer_contact?.first_name?.charAt(0) || '?'}
              </span>
            </div>
            <div className="flex-1">
              <p className="font-medium text-lg">
                {`${referredBy.referrer_contact?.first_name || ''} ${referredBy.referrer_contact?.last_name || ''}`.trim()}
              </p>
              {referredBy.referrer_contact?.primary_email && (
                <p className="text-sm text-muted-foreground">
                  {referredBy.referrer_contact.primary_email}
                </p>
              )}
            </div>
            {referredBy.context && (
              <Badge variant="outline" className="text-sm">
                {getContextLabel(referredBy.context)}
              </Badge>
            )}
            <Badge variant="secondary">
              {new Date(referredBy.created_at!).toLocaleDateString('pt-BR')}
            </Badge>
          </div>
        </Card>
      )}

      {/* Métricas de Performance */}
      {(totalContactReferrals > 0 || totalDealReferrals > 0) && referrerRevenue && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4">
            <div className="flex items-center gap-3">
              <Users className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-muted-foreground">Contatos Indicados</p>
                <p className="text-2xl font-bold">{totalContactReferrals}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <Handshake className="h-8 w-8 text-purple-600" />
              <div>
                <p className="text-sm text-muted-foreground">Deals Influenciados</p>
                <p className="text-2xl font-bold">{totalDealReferrals}</p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <DollarSign className="h-8 w-8 text-green-600" />
              <div>
                <p className="text-sm text-muted-foreground">{t('referrals.dealValue')}</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(referrerRevenue.total_deal_value || 0)}
                </p>
              </div>
            </div>
          </Card>

          <Card className="p-4">
            <div className="flex items-center gap-3">
              <TrendingUp className="h-8 w-8 text-yellow-600" />
              <div>
                <p className="text-sm text-muted-foreground">{t('referrals.revenueReceived')}</p>
                <p className="text-2xl font-bold">
                  {formatCurrency(referrerRevenue.total_revenue_received || 0)}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Lista de Indicações com Tabs */}
      <Card className="p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Indicações</h3>
          <AddReferralDialog 
            contactId={contactId} 
            onAdd={createReferral} 
            onDealCreated={handleDealCreated}
            onDealReferralCreated={createDealReferral}
          />
        </div>

        <Tabs defaultValue="contacts" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-4">
            <TabsTrigger value="contacts" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Contatos ({totalContactReferrals})
            </TabsTrigger>
            <TabsTrigger value="deals" className="flex items-center gap-2">
              <Handshake className="h-4 w-4" />
              Deals ({totalDealReferrals})
            </TabsTrigger>
          </TabsList>

          {/* Tab: Contatos Indicados */}
          <TabsContent value="contacts" className="space-y-3">
            {contactReferralsMade.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum contato indicado ainda</p>
                <p className="text-sm">Contatos novos trazidos por esta pessoa</p>
              </div>
            ) : (
              contactReferralsMade.map((referral) => {
                const contact = referral.referred_contact;
                const displayName = `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim() || 'Sem nome';

                return (
                  <div
                    key={referral.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors cursor-pointer"
                    onClick={() => navigate(`/contactdetail?id=${contact?.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <span className="text-sm font-medium text-blue-600">
                          {contact?.first_name?.charAt(0) || '?'}
                        </span>
                      </div>
                      <div>
                        <p className="font-medium">{displayName}</p>
                        <div className="flex items-center gap-2">
                          {referral.context && (
                            <Badge variant="outline" className="text-xs">
                              {getContextLabel(referral.context)}
                            </Badge>
                          )}
                          {referral.is_active ? (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Ativo
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400">
                              <XCircle className="h-3 w-3 mr-1" />
                              Expirado
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <Badge variant="secondary">
                      {new Date(referral.created_at!).toLocaleDateString('pt-BR')}
                    </Badge>
                  </div>
                );
              })
            )}
          </TabsContent>

          {/* Tab: Deals Influenciados */}
          <TabsContent value="deals" className="space-y-3">
            {dealReferralsMade.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Handshake className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>Nenhum deal influenciado ainda</p>
                <p className="text-sm">Deals específicos de contatos existentes</p>
              </div>
            ) : (
              dealReferralsMade.map((referral) => {
                const contact = referral.referred_contact;
                const deal = referral.deal;
                const displayName = `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim() || 'Sem nome';

                return (
                  <div
                    key={referral.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted/70 transition-colors cursor-pointer"
                    onClick={() => deal?.id && navigate(`/deals/${deal.id}`)}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                        <Handshake className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium">{displayName}</p>
                        <div className="flex items-center gap-2 mt-1">
                          {deal && (
                            <>
                              <Badge className={`text-xs ${getDealStageColor(deal.stage)}`}>
                                {deal.stage}
                              </Badge>
                              {deal.total_amount && (
                                <span className="text-xs text-muted-foreground">
                                  {formatCurrency(deal.total_amount)}
                                </span>
                              )}
                            </>
                          )}
                          {referral.is_active ? (
                            <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Ativo
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400">
                              <XCircle className="h-3 w-3 mr-1" />
                              {referral.deactivation_reason === 'expired_time' ? 'Expirado (tempo)' : 
                               referral.deactivation_reason === 'max_reactivations' ? 'Expirado (reativações)' : 
                               'Expirado'}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge variant="secondary">
                        {new Date(referral.created_at!).toLocaleDateString('pt-BR')}
                      </Badge>
                      {referral.revenue_share_percentage && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {referral.revenue_share_percentage}% comissão
                        </p>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </TabsContent>
        </Tabs>
      </Card>

      {/* Deal Created Success Dialog */}
      <AlertDialog open={showDealCreatedDialog} onOpenChange={setShowDealCreatedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Deal Criado com Sucesso!</AlertDialogTitle>
            <AlertDialogDescription>
              O deal foi criado como indicação. O que deseja fazer agora?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Continuar Aqui</AlertDialogCancel>
            <AlertDialogAction onClick={() => navigate(`/deals/${createdDealId}`)}>
              Ver o Deal
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

type DialogStep = 'choose' | 'create' | 'search';

interface AddReferralDialogProps {
  contactId: string;
  onAdd: (data: any) => Promise<any>;
  onDealCreated: (dealId: string) => void;
  onDealReferralCreated?: (data: { referrerContactId: string; referredContactId: string; dealId: string; context?: string }) => Promise<any>;
}

function AddReferralDialog({ contactId, onAdd, onDealCreated, onDealReferralCreated }: AddReferralDialogProps) {
  const { t } = useTranslation();
  const { createContactWithReferral, checkDuplicateContact } = useReferrals(contactId);
  const { salesUsers, isLoading: isLoadingSalesUsers } = useSalesUsers();
  
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<DialogStep>('choose');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [duplicateAlertMessage, setDuplicateAlertMessage] = useState('');
  
  // Search mode state
  const [referredContactId, setReferredContactId] = useState('');
  const [context, setContext] = useState<string>('');
  
  // Create mode state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  
  // Duplicate detection state
  const [duplicateResult, setDuplicateResult] = useState<DuplicateCheckResult | null>(null);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  
  // SDR/Consultant selection state for duplicate deal creation
  const [selectedSdr, setSelectedSdr] = useState<string>('');
  const [selectedConsultant, setSelectedConsultant] = useState<string>('');

  // Filter users by role
  const sdrUsers = salesUsers.filter(u => u.system_role?.code === 'sales_dev');
  const consultantUsers = salesUsers.filter(u => u.system_role?.code === 'sales_consultant' || u.system_role?.code === 'sales_manager');

  const resetForm = () => {
    setStep('choose');
    setReferredContactId('');
    setContext('');
    setFirstName('');
    setLastName('');
    setPhone('');
    setEmail('');
    setDuplicateResult(null);
    setSelectedSdr('');
    setSelectedConsultant('');
    setIsSubmitting(false);
    setSaving(false);
    setDuplicateAlertMessage('');
  };

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (!isOpen) {
      resetForm();
    }
  };

  const handleSearchSubmit = async () => {
    if (!referredContactId) {
      toast.error('Selecione um contato');
      return;
    }

    // Validate: must have at least SDR or Consultant
    if (!selectedSdr && !selectedConsultant) {
      toast.error('Selecione pelo menos um SDR ou Consultor');
      return;
    }

    setIsSubmitting(true);
    try {
      // 1. Criar referral de contato primeiro
      await onAdd({
        referrer_contact_id: contactId,
        referred_contact_id: referredContactId,
        referral_type: 'contact',
        context: context || undefined,
      });

      // 2. Verificar se contato já tem deals
      const { data: existingDeals } = await supabase
        .from('deals')
        .select('id')
        .eq('contact_id', referredContactId)
        .limit(1);

      // 3. Se não tem deals, criar um novo
      if (!existingDeals || existingDeals.length === 0) {
        const dealStage = selectedConsultant ? 'scheduled' : 'new';

        const { data: newDeal, error: dealError } = await supabase
          .from('deals')
          .insert({
            contact_id: referredContactId,
            deals_source: 'Customer Referral',
            source: 'direct',
            stage: dealStage,
            referred_by_contact_id: contactId,
            sales_development: selectedSdr || null,
            sales_consultant: selectedConsultant || null,
          })
          .select('id')
          .single();

        if (dealError) {
          console.error('Error creating deal:', dealError);
          toast.error('Indicação registrada, mas erro ao criar Deal');
          handleOpenChange(false);
          return;
        }

        // 4. Criar referral de deal também
        if (newDeal && onDealReferralCreated) {
          await onDealReferralCreated({
            referrerContactId: contactId,
            referredContactId: referredContactId,
            dealId: newDeal.id,
            context: context || 'other',
          });
        }

        toast.success(`Indicação registrada! Deal criado em ${dealStage === 'scheduled' ? 'Agendamento' : 'Novo'}`);
        handleOpenChange(false);
        onDealCreated(newDeal.id);
      } else {
        toast.info('Indicação registrada. Contato já possui deals existentes.');
        handleOpenChange(false);
      }
    } catch (error) {
      console.error('Error in handleSearchSubmit:', error);
      // Error handled in hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateSubmit = async () => {
    if (!firstName.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    // Validate: must have at least SDR or Consultant
    if (!selectedSdr && !selectedConsultant) {
      toast.error('Selecione pelo menos um SDR ou Consultor');
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Check for duplicates locally first
      const duplicate = await checkDuplicateContact(phone || undefined, email || undefined);
      
      if (duplicate.exists && duplicate.contact) {
        setDuplicateResult(duplicate);
        setShowDuplicateAlert(true);
        setIsSubmitting(false);
        return;
      }

      // No duplicate found locally, try to create contact with referral + deal
      const result = await createContactWithReferral({
        contactData: {
          first_name: firstName.trim(),
          last_name: lastName.trim() || undefined,
          primary_phone: phone || undefined,
          primary_email: email || undefined,
          context: (context as any) || undefined,
          sales_development_id: selectedSdr || undefined,
          sales_consultant_id: selectedConsultant || undefined,
        },
        referrerContactId: contactId,
      });
      
      handleOpenChange(false);
      
      // Se criou deal, mostrar dialog de sucesso
      if (result?.deal) {
        onDealCreated(result.deal.id);
      }
    } catch (error: any) {
      // Se o TRIGGER do banco detectou duplicata
      if (error?.type === 'DUPLICATE_TRIGGER' && error?.contactId) {
        // Buscar dados do contato duplicado
        const { data: dupContact } = await supabase
          .from('contacts')
          .select('id, first_name, last_name, primary_email, primary_phone')
          .eq('id', error.contactId)
          .single();
          
        if (dupContact) {
          setDuplicateResult({ exists: true, contact: dupContact });
          setShowDuplicateAlert(true);
        } else {
          toast.error('Contato com este telefone já existe');
        }
      }
      // Outros erros já são tratados no hook
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDuplicateCreateDeal = async () => {
    if (!duplicateResult?.contact) return;
    
    // Validate: must have at least SDR or Consultant
    if (!selectedSdr && !selectedConsultant) {
      toast.error('Selecione pelo menos um SDR ou Consultor');
      return;
    }
    
    setShowDuplicateAlert(false);
    setSaving(true);
    setDuplicateAlertMessage('');
    
    try {
      const refContactId = duplicateResult.contact.id;
      
      // 1. Determinar stage: scheduled se tem consultor, new se não
      const dealStage = selectedConsultant ? 'scheduled' : 'new';
      
      // 2. Criar o Deal com source = Customer Referral
      const { data: newDeal, error: dealError } = await supabase
        .from('deals')
        .insert({
          contact_id: refContactId,
          deals_source: 'Customer Referral',
          source: 'direct',
          stage: dealStage,
          referred_by_contact_id: contactId,
          sales_development: selectedSdr || null,
          sales_consultant: selectedConsultant || null,
        })
        .select('id')
        .single();
      
      if (dealError) {
        console.error('Error creating deal:', dealError);
        toast.error('Erro ao criar Deal');
        return;
      }
      
      // 3. Criar referral de DEAL (não de contato)
      if (onDealReferralCreated) {
        await onDealReferralCreated({
          referrerContactId: contactId,
          referredContactId: refContactId,
          dealId: newDeal.id,
          context: context || 'other',
        });
      }
      
      toast.success(`Deal criado em ${dealStage === 'scheduled' ? 'Agendamento' : 'Novo'}!`);
      
      // 4. Fechar modal principal e notificar componente pai
      handleOpenChange(false);
      onDealCreated(newDeal.id);
      
    } catch (error) {
      console.error('Error in handleDuplicateCreateDeal:', error);
      toast.error('Erro ao processar indicação');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogTrigger asChild>
          <Button size="sm" variant="outline">
            <Plus className="h-4 w-4 mr-2" />
            {t('referrals.newReferral')}
          </Button>
        </DialogTrigger>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t('referrals.dialog.title')}</DialogTitle>
            <DialogDescription>
              {step === 'choose' && 'Escolha como deseja registrar a indicação'}
              {step === 'create' && 'Preencha os dados do novo contato'}
              {step === 'search' && 'Busque um contato existente'}
            </DialogDescription>
          </DialogHeader>

          {/* Step: Choose */}
          {step === 'choose' && (
            <div className="space-y-3 py-4">
              <Button
                variant="outline"
                className="w-full h-auto p-4 flex items-start gap-3 justify-start"
                onClick={() => setStep('create')}
              >
                <UserPlus className="h-5 w-5 text-primary mt-0.5" />
                <div className="text-left">
                  <p className="font-medium">{t('referrals.dialog.createNewContact')}</p>
                  <p className="text-sm text-muted-foreground">{t('referrals.dialog.createContactDesc')}</p>
                </div>
              </Button>
              
              <Button
                variant="outline"
                className="w-full h-auto p-4 flex items-start gap-3 justify-start"
                onClick={() => setStep('search')}
              >
                <Search className="h-5 w-5 text-primary mt-0.5" />
                <div className="text-left">
                  <p className="font-medium">{t('referrals.dialog.searchExisting')}</p>
                  <p className="text-sm text-muted-foreground">{t('referrals.dialog.searchContactDesc')}</p>
                </div>
              </Button>
            </div>
          )}

          {/* Step: Create New Contact */}
          {step === 'create' && (
            <div className="space-y-4 py-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep('choose')}
                className="mb-2 -ml-2"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t('referrals.dialog.back')}
              </Button>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>{t('referrals.dialog.contactForm.firstName')} *</Label>
                  <Input
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="João"
                  />
                </div>
                <div>
                  <Label>{t('referrals.dialog.contactForm.lastName')}</Label>
                  <Input
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    placeholder="Silva"
                  />
                </div>
              </div>

              <div>
                <Label>{t('referrals.dialog.contactForm.phone')}</Label>
                <Input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="+1 555 123 4567"
                />
              </div>

              <div>
                <Label>{t('referrals.dialog.contactForm.email')}</Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <Label>{t('referrals.dialog.referralContext')}</Label>
                <Select value={context} onValueChange={setContext}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('referrals.dialog.selectContext')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="family">{t('referrals.contexts.family')}</SelectItem>
                    <SelectItem value="friend">{t('referrals.contexts.friend')}</SelectItem>
                    <SelectItem value="coworker">{t('referrals.contexts.coworker')}</SelectItem>
                    <SelectItem value="partner">{t('referrals.contexts.partner')}</SelectItem>
                    <SelectItem value="other">{t('referrals.contexts.other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* SDR/Consultant Selection */}
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-medium">Atribuição do Deal:</p>
                
                <div className="space-y-1.5">
                  <Label className="text-sm">SDR (Sales Development)</Label>
                  <Select value={selectedSdr} onValueChange={setSelectedSdr}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o SDR" />
                    </SelectTrigger>
                    <SelectContent>
                      {sdrUsers.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-sm">Consultor de Vendas</Label>
                  <Select value={selectedConsultant} onValueChange={setSelectedConsultant}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o Consultor" />
                    </SelectTrigger>
                    <SelectContent>
                      {consultantUsers.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                  {selectedConsultant 
                    ? '✅ Com consultor: Deal será criado em PROSPECTING'
                    : selectedSdr 
                      ? '📋 Sem consultor: Deal será criado em NEW'
                      : '⚠️ Selecione pelo menos um SDR ou Consultor'
                  }
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-950/20 p-3 rounded-lg text-sm text-blue-700 dark:text-blue-300">
                <p>Este contato será criado com origem <strong>"Indicação de Cliente"</strong> e um <strong>Deal</strong> será criado automaticamente.</p>
              </div>

              <Button 
                onClick={handleCreateSubmit} 
                className="w-full"
                disabled={isSubmitting || !firstName.trim() || (!selectedSdr && !selectedConsultant)}
              >
                {isSubmitting ? 'Criando...' : t('referrals.dialog.createContact')}
              </Button>
            </div>
          )}

          {/* Step: Search Existing Contact */}
          {step === 'search' && (
            <div className="space-y-4 py-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep('choose')}
                className="mb-2 -ml-2"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                {t('referrals.dialog.back')}
              </Button>

              <div>
                <Label>{t('referrals.dialog.searchPlaceholder')}</Label>
                <ContactSearchCombobox
                  value={referredContactId}
                  onValueChange={setReferredContactId}
                  placeholder={t('referrals.dialog.searchPlaceholder')}
                  excludeContactId={contactId}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {t('referrals.dialog.searchHint')}
                </p>
              </div>

              <div>
                <Label>{t('referrals.dialog.referralContext')}</Label>
                <Select value={context} onValueChange={setContext}>
                  <SelectTrigger>
                    <SelectValue placeholder={t('referrals.dialog.selectContext')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="family">{t('referrals.contexts.family')}</SelectItem>
                    <SelectItem value="friend">{t('referrals.contexts.friend')}</SelectItem>
                    <SelectItem value="coworker">{t('referrals.contexts.coworker')}</SelectItem>
                    <SelectItem value="partner">{t('referrals.contexts.partner')}</SelectItem>
                    <SelectItem value="other">{t('referrals.contexts.other')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* SDR/Consultant Selection */}
              <div className="border-t pt-4 space-y-3">
                <p className="text-sm font-medium">Atribuição (se criar deal):</p>
                
                <div className="space-y-1.5">
                  <Label className="text-sm">SDR (Sales Development)</Label>
                  <Select value={selectedSdr} onValueChange={setSelectedSdr}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o SDR" />
                    </SelectTrigger>
                    <SelectContent>
                      {sdrUsers.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1.5">
                  <Label className="text-sm">Consultor de Vendas</Label>
                  <Select value={selectedConsultant} onValueChange={setSelectedConsultant}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione o Consultor" />
                    </SelectTrigger>
                    <SelectContent>
                      {consultantUsers.map(user => (
                        <SelectItem key={user.id} value={user.id}>
                          {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                  {selectedConsultant 
                    ? '✅ Com consultor: Deal será criado em PROSPECTING'
                    : selectedSdr 
                      ? '📋 Sem consultor: Deal será criado em NEW'
                      : '⚠️ Selecione pelo menos um SDR ou Consultor'
                  }
                </div>
                
                <p className="text-xs text-muted-foreground">
                  Se o contato já tiver deals, apenas a indicação será registrada.
                </p>
              </div>

              <Button 
                onClick={handleSearchSubmit} 
                className="w-full"
                disabled={isSubmitting || !referredContactId || (!selectedSdr && !selectedConsultant)}
              >
                {isSubmitting ? 'Registrando...' : t('referrals.dialog.register')}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Duplicate Alert Dialog with SDR/Consultant Selection */}
      <AlertDialog open={showDuplicateAlert} onOpenChange={setShowDuplicateAlert}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('referrals.dialog.duplicateFound.title')}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p>{t('referrals.dialog.duplicateFound.message')}</p>
                
                {duplicateResult?.contact && (
                  <div className="p-3 bg-muted rounded-lg">
                    <p className="font-medium">
                      {duplicateResult.contact.first_name} {duplicateResult.contact.last_name}
                    </p>
                    {duplicateResult.contact.primary_email && (
                      <p className="text-sm">{duplicateResult.contact.primary_email}</p>
                    )}
                    {duplicateResult.contact.primary_phone && (
                      <p className="text-sm">{duplicateResult.contact.primary_phone}</p>
                    )}
                  </div>
                )}
                
                <div className="border-t pt-4 space-y-3">
                  <p className="text-sm font-medium text-foreground">
                    Criar Deal de Indicação (registrado como <strong>Deal Influenciado</strong>):
                  </p>
                  
                  {/* SDR Selection */}
                  <div className="space-y-1.5">
                    <Label className="text-sm">SDR (Sales Development)</Label>
                    <Select value={selectedSdr} onValueChange={setSelectedSdr}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o SDR" />
                      </SelectTrigger>
                      <SelectContent>
                        {sdrUsers.map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Consultant Selection */}
                  <div className="space-y-1.5">
                    <Label className="text-sm">Consultor de Vendas</Label>
                    <Select value={selectedConsultant} onValueChange={setSelectedConsultant}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o Consultor" />
                      </SelectTrigger>
                      <SelectContent>
                        {consultantUsers.map(user => (
                          <SelectItem key={user.id} value={user.id}>
                            {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Stage info */}
                  <div className="text-xs text-muted-foreground bg-muted/50 p-2 rounded">
                    {selectedConsultant 
                      ? '✅ Com consultor: Deal será criado em PROSPECTING'
                      : selectedSdr 
                        ? '📋 Sem consultor: Deal será criado em NEW'
                        : '⚠️ Selecione pelo menos um SDR ou Consultor'
                    }
                  </div>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setSelectedSdr('');
              setSelectedConsultant('');
            }}>
              {t('referrals.dialog.duplicateFound.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDuplicateCreateDeal}
              disabled={(!selectedSdr && !selectedConsultant) || saving}
            >
              {saving ? 'Criando...' : t('referrals.dialog.duplicateFound.createDeal')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
